/* eslint-disable react-hooks/exhaustive-deps */
import App from 'srs.sharedcomponents/lib/esm/App';
import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import {
    ApplicationInsights,
    IPageViewTelemetry,
} from '@microsoft/applicationinsights-web';
import { env } from 'srs.sharedcomponents/lib/esm/api/env';
import { store } from 'srs.sharedcomponents/lib/esm/redux/store';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'srs.sharedcomponents/lib/esm/redux/hooks';
import { setFooterLinks } from 'srs.sharedcomponents/lib/esm/redux/slices/footerLinksSlice';
import { uuidv4, stringToBoolean } from 'srs.sharedcomponents/lib/esm/utils/helpers';
import axios, { AxiosResponse } from 'axios';

import { i18nNamespaces as namespace } from 'srs.sharedcomponents/lib/esm/i18n/i18n';
import coreSrsPrimaryOverride from './i18n/coreSrsPrimaryOverride.json';
import { useTranslation } from 'react-i18next';
import { SetIsHideWarrentyOnFooter } from 'srs.sharedcomponents/lib/esm/redux/slices/footerLinksSlice';
import RouterProvider from 'srs.sharedcomponents/lib/esm/components/context/router/RouterProvider';
import routerConfig from 'srs.sharedcomponents/lib/esm/components/config/routes';
import { bearerToken } from 'srs.sharedcomponents/lib/esm/redux/slices/userSlice';
import { LogonSource } from 'srs.sharedcomponents/lib/esm/utils/enums';
import { useCookies } from 'react-cookie';
import { appConfig } from './config/app.config';

interface IAppWrapper {
    appInsights: ApplicationInsights;
}

const AppWrapper = ({ appInsights }: IAppWrapper) => {
    const { i18n } = useTranslation();
    Object.entries(coreSrsPrimaryOverride).forEach(([locale, entity]) => {
        Object.entries(entity).forEach(([key, value]) => {
            i18n.addResource(locale, namespace.coreSrsPrimary, key, value);
        });
    });

    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const [cookies] = useCookies(['LoginSource']);
    const userLoggedIn = !!useAppSelector(bearerToken);
    const user = useAppSelector((state) => state.user.user);
    const welcome = useAppSelector((state) => state.welcome);
    const footerLinks = useAppSelector((state) => state.footerLinks.links);
    const preferencesStatus = useAppSelector(
        (state) => state.prefferences.status
    );

    useEffect(() => {
        dispatch(SetIsHideWarrentyOnFooter(true));
    }, []);

    const wasLoggedIn = useRef(userLoggedIn);
    useEffect(() => {
        if (wasLoggedIn.current && !userLoggedIn) {
            const siteSettings = welcome.welcome?.siteSettings;
            const anonRedirect = siteSettings?.['Srs.AnonymousRedirectUrl'];
            const isSSOUser = cookies['LoginSource'] === LogonSource.SingleSignon;
            const ssoLoginsOnly = stringToBoolean(siteSettings?.['Srs.SSOLoginsOnly']);
            const target = (isSSOUser || ssoLoginsOnly) && anonRedirect ? anonRedirect : '/';

            if (/^https?:\/\//i.test(target)) {
                window.location.replace(target);
            } else {
                history.replace(target);
            }
        }
        wasLoggedIn.current = userLoggedIn;
    }, [userLoggedIn, history, cookies, welcome]);

    useEffect(() => {
        const myRequestInterceptor = axios.interceptors.request.use(
            (request) => {
                const { method, url, headers } = request;

                if (request.headers) {
                    request.headers['X-Correlation-Id'] = uuidv4();
                }

                if (request) {
                    appInsights.trackEvent({
                        name: 'API Request ATI',
                        properties: {
                            ApplicationName: 'ATI',
                            RequestPath: url ?? '',
                            Method: method ?? '',
                            CorrelationId: headers
                                ? headers['X-Correlation-Id']
                                : '',
                        },
                    });
                }

                return request;
            }
        );

        return () => {
            axios.interceptors.request.eject(myRequestInterceptor);
        };
    });

    useEffect(() => {
        const myResponseInterceptor = axios.interceptors.response.use(
            (response: AxiosResponse) => {
                const { config, status, statusText } = response;
                const { method, url, headers } = config;

                if (config) {
                    appInsights.trackEvent({
                        name: 'API Response ATI',
                        properties: {
                            ApplicationName: 'ATI',
                            RequestPath: url ?? '',
                            Method: method ?? '',
                            CorrelationId: headers
                                ? headers['X-Correlation-Id']
                                : '',
                            StatusCode: status,
                            StatusCodeText: statusText,
                        },
                    });
                }

                return response;
            },
            (error) => {
                // You can also log the error details in case of request failure
                appInsights.trackEvent({
                    name: 'API Error',
                    properties: {
                        message: error.message,
                        // Additional properties as needed
                    },
                });

                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(myResponseInterceptor);
        };
    });

    useEffect(() => {
        const pageInfo: IPageViewTelemetry = {
            name: location?.pathname,
            uri: window?.location.href,
            isLoggedIn: store.getState().user?.user?.token ? true : false,
        };
        if (env.APPLICATIONINSIGHTS_CONNECTION_STRING) {
            appInsights.trackPageView(pageInfo);
        }
    }, [location]);

    useEffect(() => {
        const newFooterLinks = footerLinks.filter(
            (link) => link.title !== 'Footer_HowToOrder'
        );
        const newLinks = [
            ...newFooterLinks,
            {
                id: 'howToOrder',
                route: `${welcome.welcome?.cmsBaseUrl}/UserManagedItems/Documents/Global/Footer/en-US/how_to_order.htm`,
                title: 'Footer_HowToOrder',
                target: '_blank',
            },
        ];

        dispatch(setFooterLinks(newLinks));
    }, [welcome.welcome?.cmsBaseUrl]);
useEffect(() => {
    const hideIcon = !!appConfig.hideQuickOrderRemoveIcon;

    document.body.classList.toggle(
        'hide-quick-order-remove-icon',
        hideIcon
    );

    return () => {
        document.body.classList.remove('hide-quick-order-remove-icon');
    };
}, []);
useEffect(() => {
    const hideAssigneeActions = !!appConfig.hideAssigneeActions;

    document.body.classList.toggle(
        'hide-assignee-actions',
        hideAssigneeActions
    );

    return () => {
        document.body.classList.remove('hide-assignee-actions');
    };
}, []);
    return (
        <RouterProvider routesConfig={routerConfig}>
            <App />
        </RouterProvider>
    );
    
};

export default AppWrapper;
